import { toString } from "lodash";
import FiltersBox from "PFApp/components/filters/filters_box/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import { useSavedViewsContext } from "PFApp/components/filters/saved_views_context/saved_views_context";
import { RolesTogglesControls } from "PFApp/components/roles/roles_toggles_controls";
import { SearchInput } from "PFApp/components/search_input";
import { useFirstLoad } from "PFCore/hooks/use_first_load";
import { Meta } from "PFTypes";
import { PageTarget } from "PFTypes/saved_filters";
import React, { useCallback, useEffect, useState } from "react";
import { useToggle } from "react-use";

import { WorkOpportunitiesTab } from "../../../constants";
import { useMarketplaceContext } from "../../../contexts/marketplace_context";
import { useWorkOpportunitiesTab } from "../../hooks/use_work_opportunities_tab";
import css from "./filters_area.module.scss";

type FiltersAreaProps = {
  meta: Meta;
  viewsKey: PageTarget;
  filtersAreClear: boolean;
};

export const FiltersArea = ({ meta, viewsKey, filtersAreClear }: FiltersAreaProps): JSX.Element => {
  const currentTab = useWorkOpportunitiesTab();
  const { setRelationType, handleFilterChange, handleFiltersReplace, clearAllFilters } =
    useMarketplaceContext();
  const { setSelectedViewId } = useSavedViewsContext(viewsKey);
  const [filtersOpen, toggleFiltersOpen] = useToggle(false);
  const [requirementsOn, setRequirementsOn] = useState(true);
  const [availabilityOn, setAvailabilityOn] = useState(true);
  const isFirstLoad = useFirstLoad();

  const handleToggleChange = useCallback(() => {
    setSelectedViewId(null);
    clearAllFilters();
  }, [clearAllFilters, setSelectedViewId]);

  useEffect(() => {
    if (isFirstLoad) {
      // to make Dashboard link to Unread working
      return;
    }
    setRequirementsOn(true);
    setAvailabilityOn(true);
    handleToggleChange();
    // isFirstLoad is omitted in dependency array to make Dashboard link to Unread working
    // all other deps are omitted because we want to trigger this effect only on tab switch (?)
  }, [currentTab]);

  const termValue = toString(meta.filters?.fields?.term?.value) || "";

  return (
    <div className={css.filtersArea}>
      <SearchInput
        term={termValue}
        onTermChange={(newTerm: string) => {
          const termFilter = meta.filters?.fields?.term;
          if (termFilter) {
            handleFilterChange(termFilter, newTerm);
          }
        }}
        classes={{
          input: css.searchInput
        }}
        withSearchIcon
      />
      <div className={css.rightItems}>
        {currentTab === WorkOpportunitiesTab.MatchingRoles && (
          <RolesTogglesControls
            availabilityOn={availabilityOn}
            requirementsOn={requirementsOn}
            setAvailabilityOn={(value) => {
              setAvailabilityOn(value);
              handleToggleChange();
            }}
            setRequirementsOn={(value) => {
              setRequirementsOn(value);
              handleToggleChange();
            }}
            onRelationTypeChange={setRelationType}
          />
        )}
        <FiltersButton
          filtersShown={filtersOpen}
          filtersAreClear={filtersAreClear}
          onClick={toggleFiltersOpen}
        />
      </div>
      {filtersOpen && (
        <div className={css.filtersBox}>
          <FiltersBox
            meta={meta}
            blockedList={
              currentTab === WorkOpportunitiesTab.Interested
                ? ["start_date", "interested_at", "grade"]
                : undefined
            }
            viewsKey={viewsKey}
            isRelevantToMeDisabled={requirementsOn}
            filtersAreClear={filtersAreClear}
            closeFilters={toggleFiltersOpen}
            handleFilterChange={handleFilterChange}
            handleFiltersChange={handleFiltersReplace}
            clearFilters={clearAllFilters}
          />
        </div>
      )}
    </div>
  );
};
